import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Leaf, Sun, MapPin, Ruler, Calendar, Bell, Download,
  ArrowLeft, TreePine, Flower2, Shrub, Home, Layers,
  Droplets, Wind, Bug, Sparkles, Share2, Copy, Check, TreeDeciduous
} from "lucide-react";
import GardenVisualizer from "../components/GardenVisualizer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPlan();
  }, [planId]);

  const fetchPlan = async () => {
    try {
      const response = await axios.get(`${API}/garden-plans/${planId}`);
      setPlan(response.data);
    } catch (error) {
      console.error("Failed to fetch plan:", error);
      toast.error("Failed to load garden plan");
      navigate('/saved');
    } finally {
      setLoading(false);
    }
  };

  const getLayerIcon = (layer) => {
    const icons = {
      canopy: <TreePine className="w-4 h-4" />,
      understory: <TreePine className="w-4 h-4" />,
      shrub: <Shrub className="w-4 h-4" />,
      herbaceous: <Flower2 className="w-4 h-4" />,
      ground_cover: <Leaf className="w-4 h-4" />,
      vine: <Leaf className="w-4 h-4" />,
      root: <Home className="w-4 h-4" />
    };
    return icons[layer] || <Leaf className="w-4 h-4" />;
  };

  const getLayerColor = (layer) => {
    const colors = {
      canopy: "bg-emerald-900 text-white",
      understory: "bg-emerald-700 text-white",
      shrub: "bg-green-600 text-white",
      herbaceous: "bg-lime-500 text-white",
      ground_cover: "bg-lime-300 text-gray-800",
      vine: "bg-green-400 text-gray-800",
      root: "bg-amber-600 text-white"
    };
    return colors[layer] || "bg-gray-500 text-white";
  };

  const getSunIcon = (requirement) => {
    if (requirement === "full_sun") return <Sun className="w-4 h-4 text-amber-500" />;
    if (requirement === "partial_shade") return <Sun className="w-4 h-4 text-amber-300" />;
    return <Sun className="w-4 h-4 text-gray-400" />;
  };

  const getWaterIcon = (needs) => {
    if (needs === "high") return <Droplets className="w-4 h-4 text-blue-600" />;
    if (needs === "medium") return <Droplets className="w-4 h-4 text-blue-400" />;
    return <Droplets className="w-4 h-4 text-blue-200" />;
  };

  const handleExport = () => {
    if (!plan) return;
    
    const exportData = {
      ...plan,
      exportedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${plan.name.replace(/\s+/g, '_')}_garden_plan.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success("Garden plan exported successfully!");
  };

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/share/${planId}`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Share link copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your garden plan...</p>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Garden plan not found</p>
          <Button onClick={() => navigate('/saved')} className="btn-primary">
            View Saved Plans
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/saved"
              className="text-muted-foreground hover:text-primary transition-colors"
              data-testid="back-to-saved"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-serif text-xl font-bold text-foreground" data-testid="plan-title">
                {plan.name}
              </h1>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {plan.location}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleShare}
              data-testid="share-btn"
            >
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
              {copied ? "Copied!" : "Share"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/calendar/${planId}`)}
              data-testid="calendar-btn"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Calendar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/reminders/${planId}`)}
              data-testid="reminders-btn"
            >
              <Bell className="w-4 h-4 mr-2" />
              Reminders
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/planner/${planId}`)}
              data-testid="planner-btn"
            >
              <TreeDeciduous className="w-4 h-4 mr-2" />
              Planner
            </Button>
            <Button
              size="sm"
              onClick={() => navigate(`/share/${planId}`)}
              className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-muted/50 p-1 rounded-full">
            <TabsTrigger value="overview" className="rounded-full px-6" data-testid="tab-overview">
              Overview
            </TabsTrigger>
            <TabsTrigger value="plants" className="rounded-full px-6" data-testid="tab-plants">
              Plants
            </TabsTrigger>
            <TabsTrigger value="layout" className="rounded-full px-6" data-testid="tab-layout">
              Layout
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Main Info Card */}
              <Card className="lg:col-span-2 card-organic">
                <CardHeader>
                  <CardTitle className="font-serif text-xl flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-secondary" />
                    Garden Design
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-foreground leading-relaxed" data-testid="ai-description">
                    {plan.ai_description || "Your permaculture garden design optimizes vertical space and companion planting for maximum yield and ecosystem health."}
                  </p>
                </CardContent>
              </Card>

              {/* Quick Stats */}
              <Card className="card-organic">
                <CardHeader>
                  <CardTitle className="font-serif text-xl">Garden Specs</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <Ruler className="w-4 h-4" />
                      Dimensions
                    </span>
                    <span className="font-mono font-medium">
                      {plan.width}m × {plan.length}m × {plan.height}m
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <Layers className="w-4 h-4" />
                      Ground Area
                    </span>
                    <span className="font-mono font-medium">
                      {(plan.width * plan.length).toFixed(1)} m²
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground flex items-center gap-2">
                      <TreePine className="w-4 h-4" />
                      Focus
                    </span>
                    <Badge variant="secondary" className="capitalize">
                      {plan.aesthetic}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Solar & Permaculture Info */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Solar Data */}
              {plan.solar_data && (
                <Card className="card-organic solar-metric">
                  <CardHeader>
                    <CardTitle className="font-serif text-xl flex items-center gap-2">
                      <Sun className="w-5 h-5 text-secondary" />
                      Solar Analysis
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="text-center p-4 bg-white/50 rounded-xl">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Summer</p>
                        <p className="font-mono text-3xl font-bold text-primary" data-testid="summer-sun">
                          {plan.solar_data.sun_hours_summer}h
                        </p>
                      </div>
                      <div className="text-center p-4 bg-white/50 rounded-xl">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Winter</p>
                        <p className="font-mono text-3xl font-bold text-primary" data-testid="winter-sun">
                          {plan.solar_data.sun_hours_winter}h
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-center text-muted-foreground">
                      {plan.solar_data.optimal_orientation}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Permaculture Principles */}
              <Card className="card-organic">
                <CardHeader>
                  <CardTitle className="font-serif text-xl flex items-center gap-2">
                    <Leaf className="w-5 h-5 text-primary" />
                    Permaculture Principles
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-48">
                    <ul className="space-y-2">
                      {(plan.permaculture_principles || []).map((principle, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold">
                            {i + 1}
                          </span>
                          <span>{principle}</span>
                        </li>
                      ))}
                    </ul>
                  </ScrollArea>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Plants Tab */}
          <TabsContent value="plants" className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-2xl font-bold">
                Plant Recommendations ({plan.plant_recommendations?.length || 0})
              </h2>
              <div className="flex gap-2">
                {['canopy', 'understory', 'shrub', 'herbaceous', 'ground_cover'].map(layer => (
                  <Badge key={layer} className={`${getLayerColor(layer)} text-xs`}>
                    {layer.replace('_', ' ')}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(plan.plant_recommendations || []).map((plant, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card 
                    className={`plant-card card-organic h-full layer-${plant.layer}`}
                    data-testid={`plant-card-${index}`}
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-serif font-semibold text-lg">{plant.name}</h3>
                          {plant.scientific_name && (
                            <p className="text-xs text-muted-foreground italic">
                              {plant.scientific_name}
                            </p>
                          )}
                        </div>
                        <Badge className={`${getLayerColor(plant.layer)} text-xs`}>
                          {getLayerIcon(plant.layer)}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-4 mb-3 text-sm">
                        <span className="flex items-center gap-1">
                          {getSunIcon(plant.sun_requirement)}
                          <span className="capitalize text-xs">
                            {plant.sun_requirement?.replace('_', ' ')}
                          </span>
                        </span>
                        <span className="flex items-center gap-1">
                          {getWaterIcon(plant.water_needs)}
                          <span className="capitalize text-xs">{plant.water_needs}</span>
                        </span>
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">Height:</span>
                          <span className="font-mono text-xs">{plant.height_range}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">Spacing:</span>
                          <span className="font-mono text-xs">{plant.spacing}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">Plant:</span>
                          <span className="capitalize text-xs">{plant.planting_season}</span>
                        </div>
                        {plant.harvest_season && (
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Harvest:</span>
                            <span className="capitalize text-xs">{plant.harvest_season}</span>
                          </div>
                        )}
                      </div>

                      {plant.companion_plants?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-border">
                          <p className="text-xs text-muted-foreground mb-1">Companions:</p>
                          <div className="flex flex-wrap gap-1">
                            {plant.companion_plants.slice(0, 3).map((cp, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {cp}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {plant.permaculture_function?.length > 0 && (
                        <div className="mt-3">
                          <div className="flex flex-wrap gap-1">
                            {plant.permaculture_function.map((fn, i) => (
                              <Badge 
                                key={i} 
                                variant="secondary" 
                                className="text-xs bg-primary/10 text-primary"
                              >
                                {fn.replace('_', ' ')}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* Layout Tab */}
          <TabsContent value="layout" className="space-y-6">
            <Card className="card-organic">
              <CardHeader>
                <CardTitle className="font-serif text-xl">Garden Layout Visualization</CardTitle>
              </CardHeader>
              <CardContent>
                <GardenVisualizer 
                  plan={plan} 
                  data-testid="garden-visualizer"
                />
              </CardContent>
            </Card>

            {/* Zone Legend */}
            {plan.zones?.length > 0 && (
              <Card className="card-organic">
                <CardHeader>
                  <CardTitle className="font-serif text-xl">Garden Zones</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {plan.zones.map((zone, i) => (
                      <div 
                        key={i}
                        className={`p-4 rounded-xl border border-border ${getLayerColor(zone.layer).replace('text-white', 'text-foreground').replace('text-gray-800', 'text-foreground')} bg-opacity-20`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          {getLayerIcon(zone.layer)}
                          <span className="font-medium">{zone.name}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Layer: {zone.layer.replace('_', ' ')}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Dashboard;
